import { useEffect, useMemo, useRef, useState, type DragEvent, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowUp,
  Building2,
  ChevronDown,
  ChevronRight,
  FileText,
  Folder,
  FolderInput,
  FolderOpen,
  FolderPlus,
  FolderTree,
  Loader2,
  Lock,
  Pencil,
  Plus,
  Search,
  SearchX,
  Trash2,
  Upload,
  UserRound,
} from 'lucide-react';
import { api } from '../../services/api';
import type { DocumentFolder, DocumentFolderType, DocumentStatus, DocumentType, OteDocument } from '../../types';
import { getDocumentFolderTypeLabel, getDocumentStatusLabels, getDocumentTypeLabel } from '../../types';
import ConfirmDialog from '../ui/ConfirmDialog';
import Dialog from '../ui/Dialog';
import UiField, { FormErrorSummary, FormServerError, rules, useFormValidation, type FormSchema } from '../ui/Field';
import { FormFooter } from '../projects/formBits';
import FilterBar, { type FilterDef } from '../ui/FilterBar';
import { ListsIconButton } from '../ui/ListsKit';
import DataTable, { type CardSpec, type Column, type RowAction } from '../DataTable';
import EmptyState from '../EmptyState';
import DocumentUploadModal from './DocumentUploadModal';
import DocumentDetailDrawer, { DocumentStatusBadge } from './DocumentDetailDrawer';
import ReviewDocBadge, { type ReviewDoc } from '../documentsReview/ReviewDocBadge';
import EntityPicker from '../ui/EntityPicker';
import OverflowMenu from '../details/OverflowMenu';
import { ClassificationBadge, PublishedBadge, apiErrorMessage } from '../sales/shared';
import { formatDate } from '../../utils/format';
import { checkDocumentFile, titleFromFileName } from './documentFiles';
import { acceptsFileDrop, explorerCanWrite, isFileDrag as isFileDragTypes, uploadTargetFor } from './fileDrop';
import {
  DRAG_MIME,
  buildFolderTree,
  canDropDocument,
  canDropFolder,
  childFolders,
  folderPath,
  isFrozenDocument,
  nameMatches,
  searchFolders,
  shownDocumentStatus,
  validateName,
  type DragItem,
  type DropTarget,
  type DropVerdict,
  type FolderNode,
} from './folderExplorerModel';

const FOLDER_SCHEMA: FormSchema = { 'doc-folder-name': [rules.required()] };

/** Folder types a user may choose; `contracts`/`quotes`/`general_documents` are system-only. */
const FOLDER_TYPES: DocumentFolderType[] = ['general', 'characterization', 'delivery', 'hours_reports', 'customer_files'];
const DOC_TYPES: DocumentType[] = ['general', 'quote', 'proposal', 'contract', 'delivery', 'characterization', 'report', 'checklist', 'other'];
const DOC_STATUSES: DocumentStatus[] = ['draft', 'ready_to_sign', 'sent', 'signed', 'locked'];

export interface FolderExplorerProps {
  /**
   * `all`: the tree starts with the customers (the /documents page).
   * `customer`: one customer's folders only, no customers level (the customer page tab).
   */
  mode: 'all' | 'customer';
  /** Required in `customer` mode. */
  organizationId?: string;
  /** Show the write actions (the server still decides). Default true. */
  canWrite?: boolean;
  /** `all` mode: open on this customer (`?org=`). */
  initialOrgId?: string;
  /** `all` mode: open this folder of `initialOrgId` on mount (`?folder=`, FIX-B39). */
  initialFolderId?: string | null;
  /** Open this document's drawer on mount (`?doc=`). */
  initialDocId?: string | null;
  /** Open the new-folder dialog on mount (`?createFolder=1`). */
  initialCreateFolder?: boolean;
  /** W-09: customer tab - the "documents screen" link lives in the toolbar's menu (path to open). */
  screenHref?: string;
  /** Extra toolbar content (e.g. a link to the full screen). */
  toolbarExtra?: ReactNode;
  /** Called after anything changed (upload, rename, move, folder created / deleted). */
  onChanged?: () => void;
}

type DialogState =
  | { kind: 'rename'; target: 'folder' | 'document'; id: string; name: string }
  | { kind: 'move'; target: 'folder' | 'document'; id: string; name: string }
  | null;

const nodeKeyOrg = (id: string) => `org:${id}`;
/**
 * FIX-B36 (QX-750) - a real tree: every nested level is indented (a guide line on the start
 * side), and each kind of node has its own icon: the two roots (FolderTree, bold), a customer
 * (Building2), a lead (UserRound), a folder (Folder / FolderOpen).
 */
const TREE_GROUP_CLASS = 'ms-3 space-y-0.5 border-s border-surface-200 ps-1';
const nodeKeyFolder = (id: string) => `f:${id}`;

/**
 * Folders and files like a file explorer (P-40, D-204, D-207 Q-189/Q-190):
 * a tree on the side (customers > folders, branches open with arrows, search on
 * top), the open folder's folders and files in the middle, double-click to
 * enter, an arrow-up button and breadcrumbs to leave, upload to the open
 * folder, new folder, rename, and drag (or "move to...") inside one customer.
 *
 * The server is the authority: system folders are locked there, and a move to
 * another customer or into the folder's own subtree is refused there. The
 * client only hides the drops it already knows will be refused.
 */
export default function FolderExplorer({
  mode,
  organizationId,
  canWrite: canWriteProp = true,
  initialOrgId,
  initialFolderId = null,
  initialDocId = null,
  initialCreateFolder = false,
  screenHref,
  toolbarExtra,
  onChanged,
}: FolderExplorerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const customerMode = mode === 'customer';

  const [orgId, setOrgId] = useState<string>(customerMode ? organizationId ?? '' : initialOrgId ?? '');
  const [folderId, setFolderId] = useState<string | null>(!customerMode && initialOrgId ? initialFolderId : null);
  // D-308 (QX-1319): the write actions follow the server's answer for the open customer (hidden, not disabled).
  // D-308b: for the open folder - an explicit can_edit share on it counts, as on the server.
  const writeAccessQuery = useQuery({
    queryKey: ['documentsWriteAccess', orgId, folderId],
    queryFn: () => api.getDocumentsWriteAccess(orgId, { folderId }),
    enabled: canWriteProp && !!orgId && !orgId.startsWith('lead:'),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
  const canWrite = explorerCanWrite(canWriteProp, orgId, writeAccessQuery.data?.data?.can_write);
  // FIX-B36 (QX-750) + FIX-B37 (QX-773): both roots open and close; until the user toggles one, "לקוחות" is open and "לידים" is open only
  // when the selected customer / lead is inside it, so "לידים" is not pushed below 30 customers.
  const [customersOpenPref, setCustomersOpenPref] = useState<boolean | null>(null);
  const [leadsOpenPref, setLeadsOpenPref] = useState<boolean | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(initialOrgId && !customerMode ? [nodeKeyOrg(initialOrgId)] : []));
  const [treeQuery, setTreeQuery] = useState('');
  const [docSearch, setDocSearch] = useState('');
  const [docType, setDocType] = useState('');
  const [docStatus, setDocStatus] = useState('');
  const [selectedDocId, setSelectedDocId] = useState<string | null>(initialDocId);
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [showFolderModal, setShowFolderModal] = useState(initialCreateFolder && !!(customerMode ? organizationId : initialOrgId));
  const [showUpload, setShowUpload] = useState(false);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [confirmDeleteFolder, setConfirmDeleteFolder] = useState<{ id: string; name: string } | null>(null);
  const [folderName, setFolderName] = useState('');
  const [folderType, setFolderType] = useState<DocumentFolderType>('general');
  const [folderDesc, setFolderDesc] = useState('');
  const folderForm = useFormValidation(FOLDER_SCHEMA, { 'doc-folder-name': folderName });

  // A different customer (customer page navigation) restarts at its top level.
  useEffect(() => {
    if (customerMode && organizationId) {
      setOrgId(organizationId);
      setFolderId(null);
    }
  }, [customerMode, organizationId]);

  /* ---------------- data ---------------- */
  const organizationsQuery = useQuery({
    queryKey: ['organizations', 'documents-dropdown'],
    queryFn: () => api.getOrganizations({ limit: 500 }),
    enabled: !customerMode,
  });
  const organizations = useMemo(() => organizationsQuery.data?.data ?? [], [organizationsQuery.data]);
  // P-47 (D-217 s.6): the "לידים" branch - each open lead the user may see is a pseudo-customer `lead:<id>`.
  const leadRootsQuery = useQuery({
    queryKey: ['leadFolderRoots'],
    queryFn: () => api.listLeadFolderRoots(),
    enabled: !customerMode,
  });
  const leadOrgs = useMemo(
    () => (leadRootsQuery.data?.data ?? []).map((l) => ({ id: `lead:${l.lead_id}`, name: l.name })),
    [leadRootsQuery.data],
  );

  // `parentId` null = every folder of the customer (flat), which is what the tree needs.
  const foldersQuery = useQuery({
    queryKey: ['documentFolders', orgId, 'all'],
    queryFn: () => api.listDocumentFolders(orgId, null),
    enabled: !!orgId,
  });
  const folders = useMemo<DocumentFolder[]>(() => foldersQuery.data?.data ?? [], [foldersQuery.data]);

  const docsQuery = useQuery({
    queryKey: ['documents', 'explorer', orgId],
    queryFn: () => api.listDocuments({ organizationId: orgId }),
    enabled: !!orgId,
  });
  // Folders of the other customers whose branch is open (their query is shared with `foldersQuery` when selected).
  const openOrgIds = customerMode ? [] : [...expanded].filter((k) => k.startsWith('org:')).map((k) => k.slice(4));
  const openOrgQueries = useQueries({
    queries: openOrgIds.map((id) => ({
      queryKey: ['documentFolders', id, 'all'],
      queryFn: () => api.listDocumentFolders(id, null),
    })),
  });
  // FIX-B21 (D-214 QX-191): the tree search on /documents finds folders in EVERY customer
  // the user may browse, on the server (matches + their ancestors), not only in open branches.
  const [serverQuery, setServerQuery] = useState('');
  useEffect(() => {
    const q = customerMode ? '' : treeQuery.trim();
    const timer = setTimeout(() => setServerQuery(q), 250);
    return () => clearTimeout(timer);
  }, [treeQuery, customerMode]);
  const folderSearchQuery = useQuery({
    queryKey: ['documentFolders', 'search', serverQuery],
    queryFn: () => api.searchDocumentFolders(serverQuery),
    enabled: !customerMode && serverQuery.length > 0,
    staleTime: 30_000,
  });
  const serverHits = useMemo(
    () => (serverQuery && serverQuery === treeQuery.trim() ? folderSearchQuery.data?.data ?? [] : []),
    [folderSearchQuery.data, serverQuery, treeQuery],
  );
  const foldersOf = (id: string): DocumentFolder[] => {
    if (id === orgId) return folders;
    const i = openOrgIds.indexOf(id);
    const loaded = i >= 0 ? openOrgQueries[i]?.data?.data : undefined;
    if (loaded) return loaded;
    // A branch that was never opened: while searching, the server's rows of that customer.
    return serverHits.filter((f) => f.organization_id === id);
  };
  const orgFoldersLoading = (id: string): boolean => {
    if (id === orgId) return foldersQuery.isLoading;
    const i = openOrgIds.indexOf(id);
    return i >= 0 ? !!openOrgQueries[i]?.isLoading : false;
  };
  const allDocs = useMemo<OteDocument[]>(() => docsQuery.data?.data ?? [], [docsQuery.data]);

  // If the open folder disappeared (deleted / moved away from view), go back to the top level.
  useEffect(() => {
    if (folderId && foldersQuery.isSuccess && !folders.some((f) => f.id === folderId)) setFolderId(null);
  }, [folderId, folders, foldersQuery.isSuccess]);

  const path = useMemo(() => folderPath(folders, folderId), [folders, folderId]);
  const currentFolder = path[path.length - 1] ?? null;
  const subFolders = useMemo(() => childFolders(folders, folderId), [folders, folderId]);
  // FIX-B39 (D-238): a customer's top level shows EVERY document of the customer, flat, with a "folder" column
  // (all of them sit in folders, so a top level with only direct files looked empty). Inside a folder: only its own.
  const flatRoot = customerMode && folderId === null;
  // W-09: while a document search is typed it looks in the open folder AND the folders inside it.
  const searching = docSearch.trim().length > 0;
  const docsHere = useMemo(() => {
    if (flatRoot) return allDocs;
    if (!searching) return allDocs.filter((d) => (d.folder_id ?? null) === folderId);
    const inside = new Set<string | null>([folderId]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const f of folders) if (!inside.has(f.id) && inside.has(f.parent_folder_id ?? null)) { inside.add(f.id); grew = true; }
    }
    return allDocs.filter((d) => inside.has(d.folder_id ?? null));
  }, [allDocs, folders, folderId, flatRoot, searching]);
  const showFolderCol = flatRoot || searching;

  const invalidateListings = () => {
    queryClient.invalidateQueries({ queryKey: ['documentFolders', orgId] });
    queryClient.invalidateQueries({ queryKey: ['documents'] });
    onChanged?.();
  };

  /* ---------------- navigation ---------------- */
  const goTo = (nextOrg: string, nextFolder: string | null) => {
    setOrgId(nextOrg);
    setFolderId(nextFolder);
    // DQ-291: the customer and folder live in the address (F5 / copy keep them). Only the /documents page, not the customer tab.
    if (!customerMode) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (nextOrg) next.set('org', nextOrg); else next.delete('org');
        if (nextOrg && nextFolder) next.set('folder', nextFolder); else next.delete('folder');
        next.delete('doc');
        next.delete('createFolder');
        return next;
      }, { replace: true });
    }
    setSelectedDocId((cur) => (nextOrg === orgId ? cur : null));
    setBannerError(null);
    setDocSearch('');
    setDocType('');
    setDocStatus('');
    if (nextOrg) {
      setExpanded((prev) => {
        const next = new Set(prev);
        next.add(nodeKeyOrg(nextOrg));
        // Open the chain down to the folder so the tree shows where we are.
        for (const a of folderPath(foldersOf(nextOrg), nextFolder)) if (a.id !== nextFolder) next.add(nodeKeyFolder(a.id));
        return next;
      });
    }
  };
  const goUp = () => {
    if (folderId) goTo(orgId, currentFolder?.parent_folder_id ?? null);
    else if (!customerMode && orgId) goTo('', null);
  };
  const canGoUp = !!folderId || (!customerMode && !!orgId);
  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  /* ---------------- mutations ---------------- */
  const onMutationError = (err: unknown) => setBannerError(apiErrorMessage(err, t('documents.actionError')));

  const ensureMutation = useMutation({
    mutationFn: () => api.ensureDocumentFolders(orgId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['documentFolders', orgId] }),
    onError: onMutationError,
  });

  const createFolderMutation = useMutation({
    mutationFn: () =>
      api.createDocumentFolder({
        organizationId: orgId,
        name: folderName.trim(),
        // A folder created while a folder is open becomes a child of it (§39/4b).
        parent_folder_id: folderId,
        folder_type: folderType,
        description: folderDesc.trim() || null,
      }),
    onSuccess: () => {
      invalidateListings();
      setShowFolderModal(false);
      setFolderName('');
      setFolderDesc('');
      setBannerError(null);
      folderForm.reset();
    },
    // A failure stays in the dialog (FormServerError).
  });
  const closeFolderModal = () => {
    setShowFolderModal(false);
    folderForm.reset();
    createFolderMutation.reset();
  };

  const deleteFolderMutation = useMutation({
    mutationFn: (id: string) => api.deleteDocumentFolder(id),
    onSuccess: (_d, id) => {
      invalidateListings();
      if (folderPath(folders, folderId).some((f) => f.id === id)) setFolderId(null);
      setConfirmDeleteFolder(null);
      setBannerError(null);
    },
    onError: (err) => {
      setConfirmDeleteFolder(null);
      onMutationError(err);
    },
  });

  const renameMutation = useMutation({
    mutationFn: async (v: { target: 'folder' | 'document'; id: string; name: string }): Promise<void> => {
      if (v.target === 'folder') await api.updateDocumentFolder(v.id, { name: v.name });
      else await api.updateDocument(v.id, { title: v.name });
    },
    onSuccess: () => {
      invalidateListings();
      setDialog(null);
      setBannerError(null);
    },
  });

  const moveMutation = useMutation({
    mutationFn: async (v: { target: 'folder' | 'document'; id: string; to: string | null }): Promise<void> => {
      if (v.target === 'folder') await api.updateDocumentFolder(v.id, { parent_folder_id: v.to });
      else await api.updateDocument(v.id, { folder_id: v.to });
    },
    onSuccess: (_d, v) => {
      invalidateListings();
      setDialog(null);
      setBannerError(null);
      if (v.to) setExpanded((prev) => new Set(prev).add(nodeKeyFolder(v.to as string)));
    },
    onError: onMutationError,
  });

  /* ---------------- drag and drop ---------------- */
  const dragRef = useRef<DragItem | null>(null);
  const [dropKey, setDropKey] = useState<string | null>(null);

  const verdictFor = (item: DragItem, target: DropTarget): DropVerdict => {
    if (!canWrite) return { ok: false, reason: 'same-place' };
    if (item.kind === 'folder') return canDropFolder(folders, { id: item.id, organizationId: item.organizationId }, target);
    const doc = allDocs.find((d) => d.id === item.id);
    if (!doc) return { ok: false, reason: 'same-place' };
    return canDropDocument(doc, target);
  };

  const dragHandlers = (item: DragItem, enabled: boolean) =>
    enabled
      ? {
          draggable: true,
          onDragStart: (e: DragEvent) => {
            dragRef.current = item;
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData(DRAG_MIME, JSON.stringify(item));
            e.dataTransfer.setData('text/plain', item.id);
          },
          onDragEnd: () => {
            dragRef.current = null;
            setDropKey(null);
          },
        }
      : {};

  /* ---------------- W-09b: files dragged in from the computer = upload ---------------- */
  // An internal move sets dragRef; a drag of files from the computer carries 'Files' in dataTransfer.types.
  const isFileDrag = (e: DragEvent) => isFileDragTypes(e.dataTransfer?.types, !!dragRef.current);
  const [fileDrag, setFileDrag] = useState(false);
  type UploadState = { total: number; done: number; current: string; errors: string[] } | null;
  const [uploadState, setUploadState] = useState<UploadState>(null);
  const uploadBusy = useRef(false);
  // Same path and same defaults as the "העלאה" dialog (createDocumentWithFile, general / internal, title from the file name);
  // the server still decides permissions and limits.
  const uploadFiles = async (files: File[], targetOrg: string, targetFolder: string | null) => {
    if (!canWrite || !targetOrg || files.length === 0) return;
    if (uploadBusy.current) { setBannerError(t('documents.explorer.uploadBusy')); return; }
    uploadBusy.current = true;
    setBannerError(null);
    const errors: string[] = [];
    let done = 0;
    for (const file of files) {
      setUploadState({ total: files.length, done, current: file.name, errors });
      const problem = checkDocumentFile(file);
      if (problem) {
        errors.push(t('documents.explorer.uploadFailedNamed', { name: file.name, reason: problem === 'type' ? t('documents.upload.badType') : t('documents.upload.tooLarge') }));
      } else {
        try {
          await api.createDocumentWithFile({
            organization_id: targetOrg,
            title: titleFromFileName(file.name) || file.name,
            folder_id: targetFolder,
            document_type: 'general',
            classification: 'internal',
          }, file);
        } catch (err) {
          errors.push(t('documents.explorer.uploadFailedNamed', { name: file.name, reason: apiErrorMessage(err, t('documents.actionError')) }));
        }
      }
      done += 1;
    }
    uploadBusy.current = false;
    setUploadState(errors.length ? { total: files.length, done, current: '', errors } : null);
    invalidateListings();
  };

  const dropHandlers = (target: DropTarget, key: string) => ({
    onDragOver: (e: DragEvent) => {
      if (isFileDrag(e)) {
        if (canWrite) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }
        return;
      }
      const item = dragRef.current;
      if (!item) return;
      if (verdictFor(item, target).ok) {
        e.preventDefault(); // this is a valid drop target
        e.dataTransfer.dropEffect = 'move';
      } else {
        e.dataTransfer.dropEffect = 'none';
      }
    },
    onDragEnter: (e: DragEvent) => {
      if (isFileDrag(e)) {
        if (canWrite) { e.preventDefault(); setDropKey(key); }
        return;
      }
      const item = dragRef.current;
      if (item && verdictFor(item, target).ok) {
        e.preventDefault();
        setDropKey(key);
      }
    },
    onDragLeave: (e: DragEvent) => {
      if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node | null)) setDropKey((k) => (k === key ? null : k));
    },
    onDrop: (e: DragEvent) => {
      if (isFileDrag(e)) {
        e.preventDefault();
        e.stopPropagation();
        setDropKey(null);
        setFileDrag(false);
        const dest = uploadTargetFor({ kind: 'target', target });
        void uploadFiles(Array.from(e.dataTransfer.files), dest.organizationId, dest.folderId);
        return;
      }
      const item = dragRef.current;
      dragRef.current = null;
      setDropKey(null);
      if (!item || !verdictFor(item, target).ok) return;
      e.preventDefault();
      moveMutation.mutate({ target: item.kind, id: item.id, to: target.folderId });
    },
  });

  /* ---------------- filters (documents of the open folder) ---------------- */
  const statusLabels = getDocumentStatusLabels(t);
  const docFiltersActive = !!(docSearch.trim() || docType || docStatus);
  const clearDocFilters = () => { setDocSearch(''); setDocType(''); setDocStatus(''); };
  const shownFolders = subFolders.filter((f) => nameMatches(f.name, docSearch));
  const shownDocs = docsHere.filter((d) => nameMatches(d.title ?? '', docSearch) && (!docType || d.document_type === docType) && (!docStatus || d.status === docStatus));
  const docFilters: FilterDef[] = [
    {
      key: 'type', kind: 'select', label: t('documents.type'), value: docType,
      options: DOC_TYPES.map((dt) => ({ value: dt, label: getDocumentTypeLabel(dt, t) })),
      allLabel: t('uxLists.allTypes'), onChange: setDocType,
    },
    {
      key: 'status', label: t('documents.status'), value: docStatus,
      options: DOC_STATUSES.map((s) => ({ value: s, label: statusLabels[s] })),
      allLabel: t('uxLists.allStatuses'), onChange: setDocStatus,
    },
  ];

  const folderTypeLabel = (ft: DocumentFolderType) => (FOLDER_TYPES.includes(ft) ? getDocumentFolderTypeLabel(ft, t) : t(`documents.folder_type_${ft}`, { defaultValue: ft }));

  /* ---------------- tree ---------------- */
  const treeSearching = treeQuery.trim().length > 0;
  const search = useMemo(() => searchFolders(folders, treeQuery), [folders, treeQuery]);
  const searchOf = (id: string) => (id === orgId ? search : searchFolders(foldersOf(id), treeQuery));
  const tree = useMemo(() => buildFolderTree(folders), [folders]);
  // A customer found only through a folder match (e.g. beyond the loaded customer list) is shown too.
  const orgsForTree = useMemo(() => {
    const known = new Set(organizations.map((o) => o.id));
    const extra = new Map<string, { id: string; name: string }>();
    for (const h of serverHits) if (!known.has(h.organization_id) && !extra.has(h.organization_id)) extra.set(h.organization_id, { id: h.organization_id, name: h.organization_name ?? '…' });
    return extra.size ? [...organizations, ...extra.values()] : organizations;
  }, [organizations, serverHits]);
  const shownOrgs = orgsForTree.filter((o) => nameMatches(o.name, treeQuery) || (treeSearching && searchOf(o.id).matches.size > 0));

  const dropClass = (key: string) => (dropKey === key ? 'ring-2 ring-primary-400 bg-primary-50' : '');

  const renderBranch = (nodes: FolderNode<DocumentFolder>[], level: number, srch: ReturnType<typeof searchFolders>): ReactNode =>
    nodes
      .filter((n) => !treeSearching || srch.visible.has(n.folder.id))
      .map((n) => {
        const f = n.folder;
        const key = nodeKeyFolder(f.id);
        const hasKids = n.children.some((c) => !treeSearching || srch.visible.has(c.folder.id));
        const open = hasKids && (expanded.has(key) || (treeSearching && srch.expand.has(f.id)));
        const active = orgId === f.organization_id && folderId === f.id;
        const target: DropTarget = { organizationId: f.organization_id, folderId: f.id };
        return (
          <li key={f.id} role="treeitem" aria-level={level} aria-expanded={hasKids ? open : undefined} aria-selected={active}>
            <div
              className={`group flex items-center rounded-lg transition-colors duration-150 ${active ? 'bg-primary-50 text-primary-800' : 'text-surface-700 hover:bg-surface-50'} ${dropClass(key)}`}
              {...dropHandlers(target, key)}
            >
              <TreeToggle visible={hasKids} open={open} label={f.name} onClick={() => toggle(key)} />
              <button
                type="button"
                onClick={() => goTo(f.organization_id, f.id)}
                aria-current={active ? 'page' : undefined}
                {...dragHandlers({ kind: 'folder', id: f.id, organizationId: f.organization_id }, canWrite && !f.is_system && f.organization_id === orgId)}
                className={`flex min-w-0 flex-1 items-center gap-1.5 py-1.5 pe-2 text-sm text-start ${active ? 'font-semibold' : ''}`}
              >
                {open || active
                  ? <FolderOpen className="h-4 w-4 shrink-0 text-surface-500" aria-hidden="true" />
                  : <Folder className="h-4 w-4 shrink-0 text-surface-500" aria-hidden="true" />}
                <span className="truncate"><bdi>{f.name}</bdi></span>
                {f.is_system && <span className="inline-flex shrink-0 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100" title={t('documents.explorer.systemLocked')}><Lock className="h-3 w-3 text-surface-500" aria-label={t('documents.explorer.systemLocked')} /></span>}
              </button>
            </div>
            {open && <ul role="group" className={TREE_GROUP_CLASS}>{renderBranch(n.children, level + 1, srch)}</ul>}
          </li>
        );
      });

  const renderOrgNode = (o: { id: string; name: string }, level: number, kind: 'customer' | 'lead' = 'customer'): ReactNode => {
    const key = nodeKeyOrg(o.id);
    const isSel = orgId === o.id;
    const srch = searchOf(o.id);
    const open = expanded.has(key) || (treeSearching && srch.matches.size > 0);
    const active = isSel && folderId === null;
    const orgTree = isSel ? tree : buildFolderTree(foldersOf(o.id));
    return (
      <li key={o.id} role="treeitem" aria-level={level} aria-expanded={open} aria-selected={active}>
        <div className={`flex items-center rounded-lg ${active ? 'bg-primary-50 text-primary-800' : 'text-surface-700 hover:bg-surface-50'}`}>
          <TreeToggle visible open={open} label={o.name} onClick={() => toggle(key)} />
          <button type="button" onClick={() => goTo(o.id, null)} aria-current={active ? 'page' : undefined} className="flex min-w-0 flex-1 items-center gap-1.5 py-1.5 pe-2 text-sm text-start font-medium">
            {kind === 'lead'
              ? <UserRound className="h-4 w-4 shrink-0 text-surface-500" aria-hidden="true" />
              : <Building2 className="h-4 w-4 shrink-0 text-surface-500" aria-hidden="true" />}
            <span className="truncate"><bdi>{o.name}</bdi></span>
          </button>
        </div>
        {open && (
          <ul role="group" className={TREE_GROUP_CLASS}>
            {orgFoldersLoading(o.id)
              ? <li className="px-3 py-1" aria-busy="true"><div className="skeleton h-4 w-2/3" /></li>
              : orgTree.length === 0
                ? <li className="px-3 py-1 text-xs text-surface-600">{t('documents.noFolders')}</li>
                : renderBranch(orgTree, level + 1, srch)}
          </ul>
        )}
      </li>
    );
  };

  // P-47: the "לידים" group - open leads, each with its own folders and documents (same actions as a customer).
  const renderLeadsGroup = (): ReactNode => {
    const rows = leadOrgs.filter((o) => nameMatches(o.name, treeQuery) || (treeSearching && searchOf(o.id).matches.size > 0));
    if (treeSearching && rows.length === 0) return null;
    const open = treeSearching || (leadsOpenPref ?? leadOrgs.some((o) => o.id === orgId));
    const flip = () => setLeadsOpenPref(!open);
    return (
      <li key="leads-group" role="treeitem" aria-level={1} aria-expanded={open} aria-selected={false} data-testid="leads-branch">
        <div className="flex items-center rounded-lg text-surface-700 hover:bg-surface-50">
          <TreeToggle visible open={open} label={t('documents.explorer.leadsBranch')} onClick={flip} />
          <button type="button" onClick={flip} aria-expanded={open} className="flex min-w-0 flex-1 items-center gap-1.5 py-1.5 pe-2 text-sm text-start font-semibold">
            <FolderTree className="h-4 w-4 shrink-0 text-accent-text" aria-hidden="true" />
            <span className="truncate">{t('documents.explorer.leadsBranch')}</span>
          </button>
        </div>
        {open && (
          <ul role="group" className={TREE_GROUP_CLASS}>
            {rows.map((o) => renderOrgNode(o, 2, 'lead'))}
            {rows.length === 0 && <li className="px-3 py-1 text-xs text-surface-600">{t('documents.explorer.noLeads')}</li>}
          </ul>
        )}
      </li>
    );
  };

  // P-57a (D-232 s.1): the "לקוחות" group - a virtual root node holding the customer folders (mirrors the leads group).
  const renderCustomersGroup = (): ReactNode => {
    const open = treeSearching || (customersOpenPref ?? true);
    const flip = () => setCustomersOpenPref(!open);
    return (
      <li key="customers-group" role="treeitem" aria-level={1} aria-expanded={open} aria-selected={false} data-testid="customers-branch">
        <div className="flex items-center rounded-lg text-surface-700 hover:bg-surface-50">
          <TreeToggle visible open={open} label={t('documents.explorer.customersBranch')} onClick={flip} />
          <button type="button" onClick={flip} aria-expanded={open} className="flex min-w-0 flex-1 items-center gap-1.5 py-1.5 pe-2 text-sm text-start font-semibold">
            <FolderTree className="h-4 w-4 shrink-0 text-accent-text" aria-hidden="true" />
            <span className="truncate">{t('documents.explorer.customersBranch')}</span>
          </button>
        </div>
        {open && (
          <ul role="group" className={TREE_GROUP_CLASS}>
            {shownOrgs.map((o) => renderOrgNode(o, 2, 'customer'))}
            {shownOrgs.length === 0 && !organizationsQuery.isLoading && !(treeSearching && folderSearchQuery.isFetching) && (
              <li className="px-3 py-1 text-xs text-surface-600">{t('documents.explorer.treeSearchEmpty')}</li>
            )}
            {organizationsQuery.isLoading && <li className="px-3 py-1" aria-busy="true"><div className="skeleton h-4 w-2/3" /></li>}
          </ul>
        )}
      </li>
    );
  };

  const renderTree = () => {
    if (customerMode) {
      const rootActive = folderId === null;
      return (
        <ul role="tree" aria-label={t('documents.explorer.treeLabel')} className="space-y-0.5">
          <li role="treeitem" aria-level={1} aria-selected={rootActive} aria-expanded={tree.length > 0}>
            <div
              className={`flex items-center rounded-lg ${rootActive ? 'bg-primary-50 text-primary-800' : 'text-surface-700 hover:bg-surface-50'} ${dropClass('root')}`}
              {...dropHandlers({ organizationId: orgId, folderId: null }, 'root')}
            >
              <button type="button" onClick={() => goTo(orgId, null)} aria-current={rootActive ? 'page' : undefined} className="flex min-w-0 flex-1 items-center gap-1.5 px-2 py-1.5 text-sm text-start font-medium">
                <FolderOpen className="h-4 w-4 shrink-0 text-surface-500" aria-hidden="true" />
                <span className="truncate">{t('documents.allDocuments')}</span>
              </button>
            </div>
            <ul role="group" className={TREE_GROUP_CLASS}>{renderBranch(tree, 2, search)}</ul>
          </li>
        </ul>
      );
    }
    return (
      <ul role="tree" aria-label={t('documents.explorer.treeLabel')} className="space-y-0.5">
        {renderCustomersGroup()}
        {renderLeadsGroup()}
        {treeSearching && folderSearchQuery.isFetching && (
          <li className="px-2 py-1 text-xs text-surface-600" aria-busy="true">{t('common.loading')}</li>
        )}
      </ul>
    );
  };

  /* ---------------- center ---------------- */
  const orgName = customerMode ? '' : organizations.find((o) => o.id === orgId)?.name ?? leadOrgs.find((o) => o.id === orgId)?.name ?? '';

  const crumbs: Array<{ key: string; label: ReactNode; onClick: () => void; target?: DropTarget; current: boolean }> = [];
  if (!customerMode) crumbs.push({ key: 'customers', label: t('documents.explorer.customers'), onClick: () => goTo('', null), current: !orgId });
  if (orgId) {
    if (!customerMode) crumbs.push({ key: 'org', label: orgName || '…', onClick: () => goTo(orgId, null), target: { organizationId: orgId, folderId: null }, current: !folderId });
    else crumbs.push({ key: 'root', label: t('documents.allDocuments'), onClick: () => goTo(orgId, null), target: { organizationId: orgId, folderId: null }, current: !folderId });
    for (const f of path) crumbs.push({ key: f.id, label: f.name, onClick: () => goTo(orgId, f.id), target: { organizationId: orgId, folderId: f.id }, current: f.id === folderId });
  }

  const openRename = (target: 'folder' | 'document', id: string, name: string) => {
    renameMutation.reset();
    setDialog({ kind: 'rename', target, id, name });
  };
  const openMove = (target: 'folder' | 'document', id: string, name: string) => {
    moveMutation.reset();
    setDialog({ kind: 'move', target, id, name });
  };

  const nameKeys = (onEnter: () => void, onRename: (() => void) | null) => (e: KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); onEnter(); }
    else if (e.key === 'F2' && onRename) { e.preventDefault(); onRename(); }
  };

  const rowActions = (kind: 'folder' | 'document', id: string, name: string, locked: boolean, onDelete?: () => void) => {
    if (!canWrite) return null;
    if (locked) {
      return <span className="inline-flex h-8 items-center px-2 text-surface-500 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100" title={t('documents.explorer.systemLocked')}><Lock className="h-3.5 w-3.5" aria-hidden="true" /></span>;
    }
    return (
      <span className="inline-flex items-center justify-end gap-0.5">
        <ListsIconButton label={t('documents.explorer.renameNamed', { name })} onClick={() => openRename(kind, id, name)}>
          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
        </ListsIconButton>
        <ListsIconButton label={t('documents.explorer.moveNamed', { name })} onClick={() => openMove(kind, id, name)}>
          <FolderInput className="h-3.5 w-3.5" aria-hidden="true" />
        </ListsIconButton>
        {onDelete && (
          <ListsIconButton tone="danger" label={t('uxLists.deleteItem', { name })} onClick={onDelete}>
            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
          </ListsIconButton>
        )}
      </span>
    );
  };

  // D-308c (DQ-292): per file, the server's own answer (can_edit, the rule it enforces); older rows: the folder answer.
  const docCanEdit = (d: OteDocument): boolean => (d.can_edit !== undefined ? d.can_edit : canWrite);
  const docTitleButton = (d: OteDocument) => {
    // QX-192: a signed / locked file is never renamed, but it may be moved (drag / move dialog).
    const frozen = isFrozenDocument(d);
    return (
      <button
        type="button"
        onClick={() => setSelectedDocId(d.id)}
        onKeyDown={nameKeys(() => setSelectedDocId(d.id), docCanEdit(d) && !frozen ? () => openRename('document', d.id, d.title) : null)}
        className="dt-link block max-w-[22rem] truncate text-start font-medium text-text"
        {...dragHandlers({ kind: 'document', id: d.id, organizationId: orgId }, docCanEdit(d))}
      >
        <bdi>{d.title}</bdi>
      </button>
    );
  };
  // PRESERVE #17: the files list is DataTable v2 (phone cards included), as before P-40.
  const folderCell = (d: OteDocument) => {
    if (!d.folder_id) return <span className="text-text-2">—</span>;
    const name = folders.find((f) => f.id === d.folder_id)?.name ?? d.folder_name ?? '';
    return (
      <button
        type="button"
        onClick={() => goTo(orgId, d.folder_id)}
        className="dt-link inline-flex max-w-[14rem] items-center gap-1.5 text-start text-text-2"
        title={t('documents.explorer.openFolderNamed', { name })}
      >
        <Folder className="h-3.5 w-3.5 shrink-0 text-warning-600" aria-hidden="true" />
        <span className="truncate"><bdi>{name}</bdi></span>
      </button>
    );
  };
  const docColumns: Column<OteDocument>[] = [
    { key: 'title', header: t('documents.document'), render: docTitleButton },
    ...(showFolderCol ? [{ key: 'folder', header: t('documents.explorer.colFolder'), priority: 2, width: 'w-44', render: folderCell } as Column<OteDocument>] : []),
    {
      key: 'type',
      header: t('documents.type'),
      priority: 2,
      width: 'w-36',
      render: (d) => <span className="whitespace-nowrap text-text-2">{t(`documents.type_${d.document_type}`)}</span>,
    },
    // FIX-D337 (D-337 §6): a document of the signature process shows its own state + who signed and when.
    { key: 'status', header: t('documents.status'), width: 'w-32', render: (d) => { if ((d as ReviewDoc).review_state) return <ReviewDocBadge doc={d as ReviewDoc} />; const st = shownDocumentStatus(d); return st ? <DocumentStatusBadge status={st as DocumentStatus} /> : null; } },
    {
      key: 'visibility',
      header: t('documents.visibility'),
      priority: 3,
      width: 'w-40',
      render: (d) => (
        <span className="inline-flex flex-wrap gap-1">
          <ClassificationBadge classification={d.classification} />
          {d.is_published && <PublishedBadge />}
        </span>
      ),
    },
    {
      key: 'updated',
      header: t('documents.updatedAt'),
      align: 'end',
      width: 'w-28',
      render: (d) => <span className="whitespace-nowrap text-text-2">{formatDate(d.updated_at)}</span>,
    },
  ];
  const docCard = (d: OteDocument): CardSpec => ({
    title: docTitleButton(d),
    badges: (() => { if ((d as ReviewDoc).review_state) return <ReviewDocBadge doc={d as ReviewDoc} />; const st = shownDocumentStatus(d); return st ? <DocumentStatusBadge status={st as DocumentStatus} /> : null; })(),
    metaItems: [t(`documents.type_${d.document_type}`), <span key="u" className="tabular-nums">{formatDate(d.updated_at)}</span>],
  });
  const docRowActions = (d: OteDocument): RowAction[] => {
    if (!docCanEdit(d)) return [];
    // DQ-294: the label already names the document - an explicit ariaLabel stops the table from appending the row name again.
    const move: RowAction = { key: 'move', label: t('documents.explorer.moveNamed', { name: d.title }), ariaLabel: t('documents.explorer.moveNamed', { name: d.title }), icon: <FolderInput className="h-3.5 w-3.5" aria-hidden="true" />, inline: true, onSelect: () => openMove('document', d.id, d.title) };
    if (isFrozenDocument(d)) return [move];
    return [
      { key: 'rename', label: t('documents.explorer.renameNamed', { name: d.title }), ariaLabel: t('documents.explorer.renameNamed', { name: d.title }), icon: <Pencil className="h-3.5 w-3.5" aria-hidden="true" />, inline: true, onSelect: () => openRename('document', d.id, d.title) },
      { key: 'move', label: t('documents.explorer.moveNamed', { name: d.title }), ariaLabel: t('documents.explorer.moveNamed', { name: d.title }), icon: <FolderInput className="h-3.5 w-3.5" aria-hidden="true" />, inline: true, onSelect: () => openMove('document', d.id, d.title) },
    ];
  };

  const renderRows = () => (
    <div className="space-y-4">
      {shownFolders.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-surface-200 bg-surface-0">
          <table className="table-modern w-full">
            <caption className="sr-only">{t('documents.explorer.foldersCaption')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('documents.explorer.colName')}</th>
                <th scope="col" className="hidden md:table-cell">{t('documents.type')}</th>
                <th scope="col" className="hidden sm:table-cell">{t('documents.explorer.colFiles')}</th>
                <th scope="col" className="hidden sm:table-cell text-end">{t('documents.updatedAt')}</th>
                <th scope="col"><span className="sr-only">{t('documents.explorer.actions')}</span></th>
              </tr>
            </thead>
            <tbody>
          {shownFolders.map((f) => {
            const key = nodeKeyFolder(f.id);
            const target: DropTarget = { organizationId: orgId, folderId: f.id };
            // FIX-B39: pg returns count(*) as a string unless cast; accept both.
            const n = f.document_count == null ? NaN : Number(f.document_count);
            const count = Number.isFinite(n) ? n : null;
            return (
              <tr
                key={f.id}
                className={`group ${dropClass(key)}`}
                onDoubleClick={() => goTo(orgId, f.id)}
                {...dragHandlers({ kind: 'folder', id: f.id, organizationId: orgId }, canWrite && !f.is_system)}
                {...dropHandlers(target, key)}
              >
                <td className="md:max-w-xs">
                  <button
                    type="button"
                    onClick={() => goTo(orgId, f.id)}
                    onKeyDown={nameKeys(() => goTo(orgId, f.id), canWrite && !f.is_system ? () => openRename('folder', f.id, f.name) : null)}
                    title={t('documents.explorer.openHint')}
                    className="flex max-w-full items-center gap-2 text-start font-medium text-surface-900 hover:text-primary-700"
                  >
                    <Folder className="h-4 w-4 shrink-0 text-warning-600" aria-hidden="true" />
                    <span className="truncate"><bdi>{f.name}</bdi></span>
                    {f.is_system && <span className="inline-flex shrink-0 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100" title={t('documents.explorer.systemLocked')}><Lock className="h-3 w-3 text-surface-500" aria-label={t('documents.explorer.systemLocked')} /></span>}
                  </button>
                </td>
                <td className="hidden md:table-cell text-surface-600">{folderTypeLabel(f.folder_type)}</td>
                <td className="hidden sm:table-cell text-surface-600">{count !== null ? t('documents.explorer.filesCount', { count }) : ''}</td>
                <td className="hidden sm:table-cell text-end tabular-nums whitespace-nowrap text-surface-600">{formatDate(f.updated_at)}</td>
                <td className="text-end">
                  {rowActions('folder', f.id, f.name, !!f.is_system, () => { deleteFolderMutation.reset(); setConfirmDeleteFolder({ id: f.id, name: f.name }); })}
                </td>
              </tr>
            );
          })}
            </tbody>
          </table>
        </div>
      )}
      {shownDocs.length > 0 && (
        <DataTable
          columns={docColumns}
          data={shownDocs}
          keyField="id"
          caption={currentFolder ? currentFolder.name : t('documents.allDocuments')}
          mobile="cards"
          rowLabel={(d) => d.title}
          card={docCard}
          rowActions={docRowActions}
          rowClassName={(d) => (selectedDocId === d.id ? 'bg-accent-soft' : '')}
        />
      )}
    </div>
  );

  // P-57a (D-232 s.1): no customers table in the middle - only the tree; a short hint until a folder is picked.
  const renderPickHint = () => (
    <EmptyState icon={FolderOpen} title={t('documents.explorer.pickTitle')} description={t('documents.explorer.pickDesc')} />
  );

  // W-09: ONE toolbar - the rare actions live in the "more" menu. "Create default folders" only while the customer has none of them.
  const moreItems = [
    ...(orgId && canWrite && !orgId.startsWith('lead:') && foldersQuery.isSuccess && !folders.some((f) => f.is_system)
      ? [{ key: 'ensure', label: t('documents.ensureFolders'), icon: <FolderPlus className="h-4 w-4" aria-hidden="true" />, disabled: ensureMutation.isPending, onSelect: () => ensureMutation.mutate() }]
      : []),
    ...(screenHref ? [{ key: 'screen', label: t('organization.openDocumentsScreen'), onSelect: () => navigate(screenHref) }] : []),
  ];
  const summaryText = docsQuery.isSuccess && foldersQuery.isSuccess
    ? flatRoot
      // FIX-B39: the customer's totals - every document, every folder.
      ? `${t('documents.explorer.foldersCount', { count: folders.filter((f) => nameMatches(f.name, docSearch)).length })} · ${t('uxLists.documentsCount', { count: shownDocs.length })}`
      : `${t('documents.explorer.foldersCount', { count: shownFolders.length })} · ${t('uxLists.documentsCount', { count: shownDocs.length })}`
    : null;

  const loading = foldersQuery.isLoading || docsQuery.isLoading;
  const loadError = foldersQuery.isError || docsQuery.isError;
  const emptyHere = shownFolders.length === 0 && shownDocs.length === 0;

  return (
    <div>
      {bannerError && (
        <div className="mb-4 rounded-lg bg-danger-50 px-4 py-3 text-sm text-danger-800" role="alert">{bannerError}</div>
      )}

      <div className="grid items-start gap-5 lg:grid-cols-[17rem_1fr]">
        {/* ---- Tree ---- */}
        <aside className="rounded-xl border border-surface-200 bg-surface-0 p-3" aria-label={t('documents.explorer.treeLabel')}>
          {(!customerMode || folders.length > 10 || treeSearching) && <div className="relative mb-2">
            <Search className="pointer-events-none absolute start-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-surface-500" aria-hidden="true" />
            <input
              type="search"
              value={treeQuery}
              onChange={(e) => setTreeQuery(e.target.value)}
              placeholder={customerMode ? t('documents.explorer.treeSearchFolders') : t('documents.explorer.treeSearch')}
              aria-label={customerMode ? t('documents.explorer.treeSearchFolders') : t('documents.explorer.treeSearch')}
              className="input !ps-8 text-sm"
              autoComplete="off"
            />
          </div>}
          <nav className="max-h-[28rem] overflow-y-auto" aria-label={t('documents.folders')}>
            {customerMode && !orgId ? null : renderTree()}
            {treeSearching && orgId && search.matches.size === 0 && customerMode && (
              <p className="px-2 py-2 text-xs text-surface-600">{t('documents.explorer.treeSearchEmpty')}</p>
            )}
          </nav>
        </aside>

        {/* ---- Folder content ---- */}
        <section
          className={`min-w-0 rounded-xl ${fileDrag ? 'outline-dashed outline-2 outline-offset-4 outline-primary-400 bg-primary-50/40' : ''}`}
          aria-label={t('documents.title')}
          onDragOver={(e) => { if (acceptsFileDrop(canWrite, orgId, isFileDrag(e))) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setFileDrag(true); } }}
          onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFileDrag(false); }}
          onDrop={(e) => {
            if (!acceptsFileDrop(canWrite, orgId, isFileDrag(e))) return;
            e.preventDefault();
            setFileDrag(false);
            const dest = uploadTargetFor({ kind: 'area', organizationId: orgId, folderId });
            void uploadFiles(Array.from(e.dataTransfer.files), dest.organizationId, dest.folderId);
          }}
        >
          {uploadState && (
            <div className="mb-3 rounded-lg border border-surface-200 bg-surface-0 px-4 py-3 text-sm" role="status" aria-live="polite">
              {uploadState.current && (
                <p className="inline-flex items-center gap-2 text-surface-700">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  <bdi>{t('documents.explorer.uploadingNamed', { current: uploadState.done + 1, total: uploadState.total, name: uploadState.current })}</bdi>
                </p>
              )}
              {uploadState.errors.length > 0 && (
                <ul className="mt-1 space-y-0.5 text-danger-800" role="alert">
                  {uploadState.errors.map((m, i) => <li key={i}><bdi>{m}</bdi></li>)}
                </ul>
              )}
              {!uploadState.current && (
                <button type="button" className="mt-2 underline" onClick={() => setUploadState(null)}>{t('common.close')}</button>
              )}
            </div>
          )}
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <ListsIconButton label={t('documents.explorer.up')} onClick={goUp} disabled={!canGoUp} className="border border-surface-200">
              <ArrowUp className="h-4 w-4" aria-hidden="true" />
            </ListsIconButton>
            <nav aria-label={t('documents.breadcrumb')} className="flex min-w-0 flex-1 flex-wrap items-center gap-1 text-sm">
              {crumbs.map((c, i) => (
                <span key={c.key} className="inline-flex items-center gap-1">
                  {i > 0 && <ChevronRight className="rtl-mirror h-3.5 w-3.5 text-surface-400" aria-hidden="true" />}
                  {c.current ? (
                    <span className="font-semibold text-surface-900" aria-current="page"><bdi>{c.label}</bdi></span>
                  ) : (
                    <button
                      type="button"
                      onClick={c.onClick}
                      className={`rounded px-1 text-surface-600 hover:text-primary-700 ${c.target ? dropClass(`crumb:${c.key}`) : ''}`}
                      {...(c.target ? dropHandlers(c.target, `crumb:${c.key}`) : {})}
                    >
                      <bdi>{c.label}</bdi>
                    </button>
                  )}
                </span>
              ))}
            </nav>
            {orgId && summaryText && (
              <span className="text-xs tabular-nums text-surface-500" aria-live="polite">{summaryText}</span>
            )}
            {orgId && canWrite && (
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={() => setShowUpload(true)} className="btn btn-primary btn-sm">
                  <Upload className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('documents.explorer.uploadShort')}
                </button>
                <button type="button" onClick={() => setShowFolderModal(true)} className="btn btn-secondary btn-sm">
                  <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('documents.createFolder')}
                </button>
              </div>
            )}
            {moreItems.length > 0 && <OverflowMenu items={moreItems} label={t('documents.explorer.moreActions')} variant="ghost" />}
            {toolbarExtra}
          </div>

          {!orgId ? (
            renderPickHint()
          ) : (
            <>
              <FilterBar
                className="mb-3"
                search={docSearch}
                onSearchChange={setDocSearch}
                searchPlaceholder={t('documents.explorer.searchDocs')}
                filters={docFilters}
                onClearAll={clearDocFilters}
                onClearFilters={() => { setDocType(''); setDocStatus(''); }}
              />
              {loading ? (
                <div className="space-y-2" aria-busy="true"><div className="skeleton h-9 w-full" /><div className="skeleton h-9 w-full" /><div className="skeleton h-9 w-full" /></div>
              ) : loadError ? (
                <div className="rounded-lg bg-danger-50 px-4 py-3 text-sm text-danger-800" role="alert">
                  {t('documents.actionError')}{' '}
                  <button type="button" className="underline" onClick={() => { void foldersQuery.refetch(); void docsQuery.refetch(); }}>{t('common.retry')}</button>
                </div>
              ) : emptyHere ? (
                docFiltersActive ? (
                  <EmptyState
                    icon={SearchX}
                    title={t('uxLists.noMatchesTitle')}
                    description={t('uxLists.noMatchesDesc')}
                    action={<button type="button" className="btn btn-secondary" onClick={clearDocFilters}>{t('common.clearFilter')}</button>}
                  />
                ) : (
                  <div
                    className={`rounded-xl ${dropClass(nodeKeyFolder(folderId ?? 'top'))}`}
                    {...(folderId ? dropHandlers({ organizationId: orgId, folderId }, nodeKeyFolder(folderId)) : {})}
                  >
                    <EmptyState
                      icon={FileText}
                      title={t('documents.explorer.emptyTitle')}
                      description={canWrite ? t('documents.explorer.emptyDesc') : undefined}
                    />
                  </div>
                )
              ) : renderRows()}
            </>
          )}
        </section>
      </div>

      {/* New folder */}
      <Dialog
        open={showFolderModal && canWrite}
        onClose={closeFolderModal}
        title={t('documents.createFolder')}
        size="lg"
        dismissible={!createFolderMutation.isPending}
        footer={<FormFooter formId="doc-folder-form" busy={createFolderMutation.isPending} submitLabel={t('documents.createFolderAction')} onCancel={closeFolderModal} />}
      >
        <form id="doc-folder-form" noValidate className="space-y-4" onSubmit={folderForm.submit(() => createFolderMutation.mutate())}>
          {folderForm.showSummary && (
            <FormErrorSummary errors={folderForm.errors} labels={{ 'doc-folder-name': t('documents.folderName') }} order={['doc-folder-name']} />
          )}
          <FormServerError error={createFolderMutation.error} fallback={t('documents.folderCreateFailed')} />
          <UiField id="doc-folder-name" label={t('documents.folderName')} required error={folderForm.errors['doc-folder-name']}>
            <input value={folderName} onChange={(e) => setFolderName(e.target.value)} onBlur={() => folderForm.blur('doc-folder-name')} className="input" autoComplete="off" />
          </UiField>
          <UiField id="doc-folder-type" label={t('documents.folderType')}>
            <select value={folderType} onChange={(e) => setFolderType(e.target.value as DocumentFolderType)} className="input">
              {FOLDER_TYPES.map((ft) => <option key={ft} value={ft}>{getDocumentFolderTypeLabel(ft, t)}</option>)}
            </select>
          </UiField>
          <UiField id="doc-folder-desc" label={t('documents.description')}>
            <textarea value={folderDesc} onChange={(e) => setFolderDesc(e.target.value)} rows={2} className="input" />
          </UiField>
        </form>
      </Dialog>

      {dialog?.kind === 'rename' && (
        <RenameDialog
          key={dialog.id}
          state={dialog}
          busy={renameMutation.isPending}
          error={renameMutation.error}
          onClose={() => setDialog(null)}
          onSubmit={(name) => renameMutation.mutate({ target: dialog.target, id: dialog.id, name })}
        />
      )}

      {dialog?.kind === 'move' && (
        <MoveDialog
          key={dialog.id}
          state={dialog}
          folders={folders}
          verdict={(to) => verdictFor({ kind: dialog.target, id: dialog.id, organizationId: orgId }, { organizationId: orgId, folderId: to })}
          busy={moveMutation.isPending}
          error={moveMutation.error}
          onClose={() => setDialog(null)}
          onSubmit={(to) => moveMutation.mutate({ target: dialog.target, id: dialog.id, to })}
        />
      )}

      {/* Upload to the open folder */}
      {showUpload && orgId && canWrite && (
        <DocumentUploadModal
          organizationId={orgId}
          folderId={folderId}
          onClose={() => setShowUpload(false)}
          onCreated={(created) => {
            setShowUpload(false);
            setBannerError(null);
            invalidateListings();
            setSelectedDocId(created.id);
          }}
        />
      )}

      {selectedDocId && (
        <DocumentDetailDrawer documentId={selectedDocId} canWrite={canWrite} onClose={() => setSelectedDocId(null)} onChanged={invalidateListings} />
      )}

      <ConfirmDialog
        open={!!confirmDeleteFolder}
        danger
        title={t('documents.deleteFolderTitle', { name: confirmDeleteFolder?.name ?? '' })}
        description={t('documents.deleteFolderConsequence')}
        confirmLabel={t('documents.deleteFolderAction')}
        loading={deleteFolderMutation.isPending}
        onConfirm={() => { if (confirmDeleteFolder) deleteFolderMutation.mutate(confirmDeleteFolder.id); }}
        onCancel={() => setConfirmDeleteFolder(null)}
      />
    </div>
  );
}

/** The arrow that opens / closes a tree branch (reserves the same width when there is nothing to open). */
function TreeToggle({ visible, open, label, onClick }: { visible: boolean; open: boolean; label: string; onClick: () => void }) {
  const { t } = useTranslation();
  if (!visible) return <span className="inline-block h-7 w-7 shrink-0" aria-hidden="true" />;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={t(open ? 'documents.explorer.collapseNamed' : 'documents.explorer.expandNamed', { name: label })}
      aria-expanded={open}
      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-surface-500 hover:bg-surface-100"
    >
      {/* R-08: the arrow shows the state. Closed = points to the start side ("<" in RTL); open = points down.
          (a rotate class on a .rtl-mirror icon is overridden by its scaleX(-1), so in RTL it never changed.) */}
      {open
        ? <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
        : <ChevronRight className="rtl-mirror h-3.5 w-3.5" aria-hidden="true" />}
    </button>
  );
}

function RenameDialog({
  state, busy, error, onClose, onSubmit,
}: {
  state: { target: 'folder' | 'document'; name: string };
  busy: boolean;
  error: unknown;
  onClose: () => void;
  onSubmit: (name: string) => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(state.name);
  const [touched, setTouched] = useState(false);
  const problem = validateName(value);
  const title = state.target === 'folder' ? t('documents.explorer.renameFolderTitle') : t('documents.explorer.renameFileTitle');
  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      size="md"
      dismissible={!busy}
      footer={<FormFooter formId="doc-rename-form" busy={busy} submitLabel={t('common.save')} onCancel={onClose} />}
    >
      <form
        id="doc-rename-form"
        noValidate
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          setTouched(true);
          if (!problem && value.trim() !== state.name) onSubmit(value.trim());
          else if (!problem) onClose();
        }}
      >
        <FormServerError error={error} fallback={t('documents.explorer.renameFailed')} />
        <UiField
          id="doc-rename-name"
          label={t('documents.explorer.newName')}
          required
          error={touched && problem ? t(problem === 'empty' ? 'documents.explorer.nameRequired' : 'documents.explorer.nameTooLong') : undefined}
        >
          <input value={value} onChange={(e) => setValue(e.target.value)} onBlur={() => setTouched(true)} className="input" autoComplete="off" maxLength={300} />
        </UiField>
      </form>
    </Dialog>
  );
}

function MoveDialog({
  state, folders, verdict, busy, error, onClose, onSubmit,
}: {
  state: { target: 'folder' | 'document'; name: string };
  folders: DocumentFolder[];
  verdict: (to: string | null) => DropVerdict;
  busy: boolean;
  error: unknown;
  onClose: () => void;
  onSubmit: (to: string | null) => void;
}) {
  const { t } = useTranslation();
  const options = useMemo(() => {
    const rows: Array<{ id: string | null; label: string }> = [];
    // QX-190: a file, like a folder, may go to the customer's top level.
    if (verdict(null).ok) rows.push({ id: null, label: t('documents.explorer.moveTop') });
    for (const f of folders) {
      if (!verdict(f.id).ok) continue;
      rows.push({ id: f.id, label: folderPath(folders, f.id).map((p) => p.name).join(' / ') });
    }
    return rows.sort((a, b) => (a.id === null ? -1 : b.id === null ? 1 : a.label.localeCompare(b.label, 'he')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folders, state.target]);
  const [to, setTo] = useState<string>(options[0]?.id ?? '');
  const empty = options.length === 0;
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('documents.explorer.moveTitle', { name: state.name })}
      size="md"
      dismissible={!busy}
      footer={<FormFooter formId="doc-move-form" busy={busy} disabled={empty} submitLabel={t('documents.explorer.moveAction')} onCancel={onClose} />}
    >
      <form id="doc-move-form" noValidate className="space-y-4" onSubmit={(e) => { e.preventDefault(); if (!empty) onSubmit(to || null); }}>
        <FormServerError error={error} fallback={t('documents.explorer.moveFailed')} />
        {empty ? (
          <p className="text-sm text-surface-600">{t('documents.explorer.moveNoTargets')}</p>
        ) : (
          <UiField id="doc-move-target" label={t('documents.explorer.moveTarget')}>
            <EntityPicker
              value={to}
              onChange={setTo}
              clearable={false}
              emptyLabel={options[0]?.id === null ? options[0].label : undefined}
              options={options.filter((o) => o.id !== null).map((o) => ({ value: o.id as string, label: o.label }))}
            />
          </UiField>
        )}
      </form>
    </Dialog>
  );
}
